import { useEffect, useRef, useState } from "react";
import { Minus, Plus } from "lucide-react";

/**
 * DurationPicker — 5 equal-width blocks, each rendered as a single pill.
 *
 *   ┌────────┬───────┬───────┬────────┬────────┐
 *   │ H:Min  │ Jours │ Sem.  │  Mois  │  Ans   │
 *   └────────┴───────┴───────┴────────┴────────┘
 *
 * Every block is one continuous rounded rectangle framed by a thin
 * `rgba(255,255,255,0.3)` border. The −/+ buttons and the numeric value
 * live *inside* that outline — no per-child borders. Active values tint
 * the whole pill in the current accent colour.
 */
export const EMPTY_DURATION = { minutes: 0, hours: 0, days: 0, weeks: 0, months: 0, years: 0 };

const COUNTERS = [
  { key: "days",   label: "Jours",  max: 999 },
  { key: "weeks",  label: "Sem.",   max: 999 },
  { key: "months", label: "Mois",   max: 999 },
  { key: "years",  label: "Ans",    max: 99  },
];

const PILL_BORDER = "rgba(255,255,255,0.3)";
const PILL_BG = "rgba(255,255,255,0.03)";
const PILL_HEIGHT = 34;

const pillWrapper = ({ active, accent }) => ({
  display: "inline-flex",
  alignItems: "center",
  height: PILL_HEIGHT,
  padding: "0 6px",
  gap: 7,
  borderRadius: 999,
  border: `1px solid ${active ? `${accent}66` : PILL_BORDER}`,
  background: active ? `${accent}1A` : PILL_BG,
  transition: "background-color .15s, border-color .15s",
});

const btnStyle = (accent) => ({
  width: 24,
  height: 24,
  borderRadius: 6,                          // rounded square, not a full circle
  background: accent,
  border: "none",
  color: "#FFFFFF",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "filter .15s",
  flex: "0 0 auto",
  padding: 0,
});

const valueText = ({ active, accent }) => ({
  minWidth: 22,
  textAlign: "center",
  fontSize: 14,
  fontWeight: 600,
  fontVariantNumeric: "tabular-nums",
  color: active ? accent : "#EDEDED",
  padding: "0 2px",
});

export function toMinutes(v) {
  return (
    (v.minutes || 0) +
    (v.hours   || 0) * 60 +
    (v.days    || 0) * 60 * 24 +
    (v.weeks   || 0) * 60 * 24 * 7 +
    (v.months  || 0) * 60 * 24 * 30 +
    (v.years   || 0) * 60 * 24 * 365
  );
}

export function formatDuration(v) {
  const parts = [];
  if (v.years)   parts.push(`${v.years} an${v.years > 1 ? "s" : ""}`);
  if (v.months)  parts.push(`${v.months} mois`);
  if (v.weeks)   parts.push(`${v.weeks} sem.`);
  if (v.days)    parts.push(`${v.days} j`);
  if (v.hours || v.minutes) {
    const pad = (n) => String(n).padStart(2, "0");
    parts.push(`${pad(v.hours || 0)}:${pad(v.minutes || 0)}`);
  }
  return parts.length ? parts.join(" ") : "0";
}

/** ± stepper pill — used for Jours / Sem. / Mois / Ans. */
function StepperPill({ value, onDec, onInc, active, accent, testId, max }) {
  const isZero = value === 0;
  const atMax = typeof max === "number" && value >= max;
  return (
    <div style={pillWrapper({ active, accent })} data-testid={`${testId}-pill`}>
      <button
        type="button"
        onClick={onDec}
        disabled={isZero}
        aria-label="Diminuer"
        style={btnStyle(accent)}
        className="hover:brightness-125 disabled:cursor-not-allowed"
        data-testid={`${testId}-dec`}
      >
        <Minus size={12} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      </button>
      <span style={valueText({ active, accent })} data-testid={`${testId}-value`}>{value}</span>
      <button
        type="button"
        onClick={onInc}
        disabled={atMax}
        aria-label="Augmenter"
        style={btnStyle(accent)}
        className="hover:brightness-125 disabled:cursor-not-allowed"
        data-testid={`${testId}-inc`}
      >
        <Plus size={12} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" />
      </button>
    </div>
  );
}

/** Editable HH or MM slot — borderless, sits inside the H:Min pill. */
function DigitSlot({ value, onCommit, max, active, accent, testId }) {
  const ref = useRef(null);
  const pad = (n) => String(n).padStart(2, "0");
  const [text, setText] = useState(pad(value || 0));

  useEffect(() => { setText(pad(value || 0)); }, [value]);

  const commit = () => {
    const parsed = parseInt(text, 10);
    const n = Number.isFinite(parsed)
      ? Math.min(max, Math.max(0, parsed))
      : 0;
    setText(pad(n));
    if (n !== (value || 0)) onCommit(n);
  };

  return (
    <input
      ref={ref}
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      maxLength={2}
      value={text}
      onFocus={(e) => e.target.select()}
      onChange={(e) => setText(e.target.value.replace(/\D/g, "").slice(0, 2))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") { setText(pad(value || 0)); e.currentTarget.blur(); }
      }}
      style={{
        width: 26,
        height: 22,
        padding: "0 0 2px",
        border: "none",
        borderBottom: `1px dashed ${active ? accent : "#666"}`,
        borderRadius: 0,
        background: "transparent",
        color: active ? accent : "#EDEDED",
        textAlign: "center",
        fontSize: 14,
        fontWeight: 600,
        fontVariantNumeric: "tabular-nums",
        outline: "none",
        WebkitAppearance: "none",
        appearance: "none",
        cursor: "text",
      }}
      data-testid={testId}
      data-no-capitalize
      className="dp-digit-slot"
    />
  );
}

/** H : MM pill — direct-input digit slots inside a single rounded outline.
 *  Passes the caller's testIdPrefix through to the <input> elements so the
 *  existing tests (`tc-dur-before-hours-input`, etc.) keep working. */
function HourMinutePillProxy({ value, onChange, accent, testIdPrefix }) {
  const hours = value.hours || 0;
  const minutes = value.minutes || 0;
  const active = hours > 0 || minutes > 0;
  return (
    <div style={pillWrapper({ active, accent })}>
      <DigitSlot
        value={hours}
        onCommit={(n) => onChange({ ...value, hours: n })}
        max={99}
        active={hours > 0}
        accent={accent}
        testId={`${testIdPrefix}-hours-input`}
      />
      <span
        style={{
          color: "#666",
          fontWeight: 700,
          fontSize: 14,
          userSelect: "none",
          pointerEvents: "none",
        }}
      >:</span>
      <DigitSlot
        value={minutes}
        onCommit={(n) => onChange({ ...value, minutes: n })}
        max={59}
        active={minutes > 0}
        accent={accent}
        testId={`${testIdPrefix}-minutes-input`}
      />
    </div>
  );
}

export default function DurationPicker({ value, onChange, accent = "#0078D4", testIdPrefix = "dur", hideHMin = false }) {
  const bump = (key, delta, max) => {
    const next = Math.max(0, Math.min(max, (value[key] || 0) + delta));
    onChange({ ...value, [key]: next });
  };

  return (
    <div className={hideHMin ? "grid grid-cols-4 gap-3" : "grid grid-cols-5 gap-3"} data-testid={testIdPrefix}>
      {/* Block 1 — H : MM (hidden when the caller only needs whole-day durations) */}
      {!hideHMin && (
        <div className="flex flex-col items-center gap-1.5" data-testid={`${testIdPrefix}-hmin`}>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">H : Min</span>
          {/* Give the H:Min pill the caller-specific test-id prefix by wrapping */}
          <div data-testid={`${testIdPrefix}-hmin-pill-wrap`}>
            <HourMinutePillProxy
              value={value}
              onChange={onChange}
              accent={accent}
              testIdPrefix={testIdPrefix}
            />
          </div>
        </div>
      )}

      {/* Blocks 2-5 — Jours / Sem. / Mois / Ans */}
      {COUNTERS.map(({ key, label, max }) => (
        <div key={key} className="flex flex-col items-center gap-1.5" data-testid={`${testIdPrefix}-${key}`}>
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
          <StepperPill
            value={value[key] || 0}
            onDec={() => bump(key, -1, max)}
            onInc={() => bump(key, +1, max)}
            active={(value[key] || 0) > 0}
            accent={accent}
            testId={`${testIdPrefix}-${key}`}
            max={max}
          />
        </div>
      ))}
    </div>
  );
}
